import { cloneDeep, isEmpty, omit, uniq } from 'lodash/fp';

import { getSchemaForFieldPath } from 'dashboard/components/JsonSchemaSelect';
import { traverseWorkflowSteps } from 'dashboard/components/WorkflowEditor/utils';
import {
  caseAttachmentsFor,
  generateJsonSchemaConfig,
  generateStepContext,
  generateUsableObjects,
  getLoopStepInnerSchema,
  JsonLogicField,
  UsableObjectConfig,
} from 'dashboard/lib/workflows';
import { isJsonLogicField } from 'dashboard/lib/workflows/json-logic';
import { BaseTrigger, BaseTriggerParams } from 'dashboard/models/trigger';
import { WorkflowSchema } from 'dashboard/models/workflow';
import { WorkflowBlock } from 'dashboard/models/workflow-block';
import { WorkflowCriteriaVariableSchemas } from 'dashboard/models/workflow-schema';
import {
  ActualWorkflowStep,
  FakeWorkflowStepType,
  INPUT_STEP_TOKEN,
  NullWorkflowStepActionKey,
  WorkflowActionCreateCaseV1,
  WorkflowConditional,
  WorkflowStep,
  WorkflowStepAction,
  WorkflowStepActionKey,
  WorkflowStepConditional,
  WorkflowStepInput,
  WorkflowStepLoop,
  WorkflowStepLoopMapSettings,
  WorkflowStepLoopType,
  WorkflowStepModule,
  WorkflowStepType,
  WorkflowVersion,
} from 'dashboard/models/workflow-version';
import { ObjectSchemaMap } from 'dashboard/redux/slices/object-schemas';
import { JsonSchema, JsonSchemaArray, JsonSchemaObject, JsonSchemaTypes } from 'lib/json-schema';
import { assertNever } from 'lib/utils/types';
import { buildGraph, findPath } from 'lib/workflows';

/**
 * Performs operations to make steps config UI ready:
 * - return new stepsConfig with view-specific attributes added, in a format expected by the frontend.
 * - return contexts generated for each step config
 **/
export const makeStepsConfigUIReady = ({
  steps,
  inputStep,
  workflowSchema,
  blocksByBlockId,
  includeBlockInnerContext,
  platformObjectJsonSchema,
  showErrorObjects = false,
}: {
  steps: WorkflowStep[];
  inputStep?: WorkflowStepInput;
  workflowSchema: WorkflowSchema;
  blocksByBlockId?: Record<string, WorkflowBlock>;
  includeBlockInnerContext?: boolean;
  platformObjectJsonSchema: ObjectSchemaMap | undefined;
  showErrorObjects?: boolean;
}): {
  stepContexts: Record<string, UsableObjectConfig[]>;
  stepsConfig: WorkflowStep[];
} => {
  const graph = buildGraph(steps);
  const criteriaVariableSchemas = workflowSchema['criteria-variable-schemas'];
  const genericWorkflowActions = workflowSchema['generic-workflow-actions'];
  const stepContexts: Record<string, UsableObjectConfig[]> = inputStep
    ? {
        [inputStep.token]: generateStepContext({
          step: inputStep,
          criteriaVariableSchemas,
          genericWorkflowActions,
          platformObjectJsonSchema,
          showErrorObjects,
        }),
      }
    : {};

  const getSchemaFromStepContexts = (path: string) => {
    if (!path) {
      return null;
    }

    const token = path.split('.')[0];
    const key = [BaseTrigger, BaseTriggerParams].includes(token) ? INPUT_STEP_TOKEN : token;
    if (!stepContexts[key] && !graph[key]) {
      return null;
    }

    let usableObjects = stepContexts[key];
    if (!usableObjects && graph[key]?.step) {
      const step = graph[key].step;
      // If the context hasn't been generated yet, generate in place here.
      // This is used for loop map step `item-path` schema, where the `item-path` references a step within the loop branch.
      // At this point we are on the loop step itself.
      // Because we generate contexts as we traverse, the loop branch contexts haven't been generated yet.
      usableObjects = generateStepContext({
        step,
        criteriaVariableSchemas,
        genericWorkflowActions,
        platformObjectJsonSchema,
        showErrorObjects,
      });
    }
    const { jsonSchema } = generateJsonSchemaConfig({
      usableObjects,
      useOriginal: true,
    });
    return getSchemaForFieldPath({
      path,
      initialSchema: jsonSchema,
      primitiveUtilities: workflowSchema.primitives,
    }).schema;
  };

  const callback = (step: WorkflowStep): { step: WorkflowStep } => {
    switch (step.type) {
      case WorkflowStepType.Action:
      case WorkflowStepType.Placeholder:
      case WorkflowStepType.Wait:
      case FakeWorkflowStepType.MapOutput:
      case WorkflowStepType.Conditional:
      case WorkflowStepType.Parallel: {
        stepContexts[step.token] = generateStepContext({
          step,
          criteriaVariableSchemas,
          genericWorkflowActions,
          platformObjectJsonSchema,
          showErrorObjects,
        });
        return { step };
      }
      case WorkflowStepType.Module: {
        stepContexts[step.token] = generateStepContext({
          step,
          criteriaVariableSchemas,
          genericWorkflowActions,
          moduleBlock:
            blocksByBlockId && step.type === WorkflowStepType.Module
              ? blocksByBlockId[step['child-workflow-block-id']]
              : undefined,
          includeBlockInnerContext,
          platformObjectJsonSchema,
          showErrorObjects,
        });
        return { step };
      }
      case WorkflowStepType.Loop: {
        let stepToReturn = cloneDeep(step);

        if (
          stepToReturn.settings.type === WorkflowStepLoopType.ForEach &&
          !stepToReturn.settings['input-array-schema']
        ) {
          const inputArraySchema = getSchemaFromStepContexts(stepToReturn.settings['array-path']);

          if (inputArraySchema) {
            stepToReturn.settings['input-array-schema'] = inputArraySchema as JsonSchemaArray;
          }
        }
        if (stepToReturn.settings.type === WorkflowStepLoopType.Map) {
          const settings: WorkflowStepLoopMapSettings = stepToReturn.settings;
          const inputArraySchema =
            settings['input-array-schema'] || getSchemaFromStepContexts(settings['array-path']);

          if (inputArraySchema) {
            settings['input-array-schema'] = inputArraySchema as JsonSchemaArray;
          }

          if (!settings['output-step-token']) {
            const outputStep = {
              type: FakeWorkflowStepType.MapOutput,
              token: `${stepToReturn.token}-map-output`,
              name: 'Array Map Output',
            };

            let outputItemSchema: JsonSchema | null;
            if (inputArraySchema && settings['item-path'].includes(step.token)) {
              // this means the item path is on the inner context provided by loop step (e.g. current_index, current_item).
              // get portion of path starting from step token.
              const nestedPath = settings['item-path'].slice(
                settings['item-path'].indexOf(step.token),
              );
              const innerSchema: JsonSchemaObject = {
                type: JsonSchemaTypes.Object,
                properties: {
                  [stepToReturn.token]: getLoopStepInnerSchema(stepToReturn),
                },
              };
              outputItemSchema = getSchemaForFieldPath({
                path: nestedPath,
                initialSchema: innerSchema,
                primitiveUtilities: workflowSchema.primitives,
              }).schema;
            } else {
              outputItemSchema = getSchemaFromStepContexts(settings['item-path']);
            }

            const mapSettings = {
              ...settings,
              'output-step-token': outputStep.token,
            };

            if (outputItemSchema) {
              mapSettings['output-item-schema'] = outputItemSchema;
            }

            stepToReturn = {
              ...stepToReturn,
              branch: [...stepToReturn.branch, outputStep],
              settings: mapSettings,
            };
          }
        }

        stepContexts[stepToReturn.token] = generateStepContext({
          step: stepToReturn,
          criteriaVariableSchemas,
          genericWorkflowActions,
          platformObjectJsonSchema,
          showErrorObjects,
        });
        return { step: stepToReturn };
      }
      default:
        assertNever(step);
    }
  };
  return {
    stepContexts,
    stepsConfig: traverseWorkflowSteps(steps, callback),
  };
};

/**
 *
 * Return new stepsConfig with view-specific attributes stripped, in a format expected by the backend.
 * @param steps: stepsConfig from WorkflowVersion
 *
 **/
export const stripViewSpecificAttributes = (
  steps: WorkflowVersion['attributes']['steps-config'],
): WorkflowStep[] => {
  return steps
    .filter((step): step is ActualWorkflowStep => {
      return step.type !== FakeWorkflowStepType.MapOutput;
    })
    .map((step) => {
      switch (step.type) {
        case WorkflowStepType.Action:
        case WorkflowStepType.Module:
        case WorkflowStepType.Wait:
          return step;
        case WorkflowStepType.Conditional:
        case WorkflowStepType.Parallel:
          return {
            ...step,
            branches: step.branches.map((branch) => stripViewSpecificAttributes(branch)),
          };
        case WorkflowStepType.Loop: {
          let settings = step.settings;
          if (step.settings.type === WorkflowStepLoopType.Map) {
            settings = omit(['input-array-schema', 'output-step-token', 'output-item-schema'], {
              ...step.settings,
            });
          } else if (step.settings.type === WorkflowStepLoopType.ForEach) {
            settings = omit(['input-array-schema'], { ...step.settings });
          }
          return {
            ...step,
            branch: stripViewSpecificAttributes(step.branch),
            settings,
          };
        }
        default:
          assertNever(step);
      }
    });
};

const replaceTokenReference = (original: string, tokenMap: Map<string, string>): string => {
  if (!original) {
    return original;
  }
  // There are three potential cases here
  // 1. a singular step token (hash)
  // 2. a step token and subpath (hash.fields.id)
  // 3. a handlebar text that includes variable substitution ({{hash}} and {{hash.field.id}}).
  tokenMap.forEach((newToken, oldToken) => {
    // This could cause unexpected behaviour if the field contains substring that coincides with the old token, but are not meant for field substitution.
    // However, given that the tokens are randomly generated strings, the chances are fairly low.
    original = original.replace(new RegExp(oldToken, 'g'), newToken);
  });
  return original;
};

export const replaceReferencesInParams = (
  steps: WorkflowStep[],
  tokenMap: Map<string, string>,
): WorkflowStep[] => {
  return traverseWorkflowSteps(steps, (step) => {
    switch (step.type) {
      case WorkflowStepType.Action:
        if (step.action.key === NullWorkflowStepActionKey.NullKey) {
          return { step };
        }
        return {
          step: {
            ...step,
            action: {
              ...step.action,
              params: JSON.parse(
                replaceTokenReference(JSON.stringify(step.action.params), tokenMap),
              ),
            },
          },
        };
      case WorkflowStepType.Loop:
        return {
          step: {
            ...step,
            settings: updateReferencesForLoopSetting(step.settings, tokenMap),
          },
          checkChildren: true,
        };
      // in a conditional or parallel step, check for steps to update in the children
      case WorkflowStepType.Conditional:
        return {
          step: {
            ...step,
            conditionals: updateReferencesForConditionals(step.conditionals, tokenMap),
          },
          checkChildren: true,
        };
      case WorkflowStepType.Module:
        if (isEmpty(step['input-mappings'])) {
          return { step };
        }
        return {
          step: {
            ...step,
            'input-mappings': step['input-mappings'].map((mapping) => ({
              ...mapping,
              'method-chain': mapping['method-chain']
                ? replaceTokenReference(mapping['method-chain'], tokenMap)
                : null,
            })),
          },
        };
      case WorkflowStepType.Parallel:
        return { step, checkChildren: true };
      case FakeWorkflowStepType.MapOutput:
      case WorkflowStepType.Wait:
      case WorkflowStepType.Placeholder:
        return { step };
      default:
        assertNever(step);
    }
  });
};

interface UpdateReferencesInput {
  currentSteps: WorkflowStep[];
  targetReference: WorkflowStep;
  inputStep: WorkflowStepInput;
  criteriaVariableSchemas: WorkflowCriteriaVariableSchemas;
  checkUpdate?: boolean;
}

/**
 * Adds references to newly created objects.
 * @param currentSteps: an original stepsConfig
 * @param targetReference: the newly updated step
 * @param inputStep: input step of workflow
 * @param pathToConfig: use for recursion to get the path to that config
 */
export const updateReferences = ({
  currentSteps,
  targetReference,
  inputStep,
  criteriaVariableSchemas,
  checkUpdate = true,
}: UpdateReferencesInput): WorkflowStep[] => {
  return traverseWorkflowSteps(currentSteps, (step) => {
    if (step.type === WorkflowStepType.Action) {
      if (step.action.key === NullWorkflowStepActionKey.NullKey) {
        return { step };
      }
      if (step.action.key === WorkflowStepActionKey.CreateCase) {
        return {
          step: updateReferenceForCreateCase({
            currentSteps,
            inputStep,
            targetReference,
            step,
            criteriaVariableSchemas,
            checkUpdate,
          }),
        };
      }
    }
    return { step, checkChildren: true };
  });
};

const updateReferenceForCreateCase = ({
  currentSteps,
  inputStep,
  targetReference,
  step,
  criteriaVariableSchemas,
  checkUpdate,
}: {
  currentSteps: WorkflowStep[];
  inputStep: WorkflowStepInput;
  targetReference: WorkflowStep;
  step: WorkflowStepAction;
  criteriaVariableSchemas: WorkflowCriteriaVariableSchemas;
  checkUpdate: boolean;
}): WorkflowStepAction => {
  // check that the targetReference is an action
  if (
    targetReference.type !== WorkflowStepType.Action ||
    step.action.key !== WorkflowStepActionKey.CreateCase
  ) {
    return step;
  }
  // check that the action key is updated
  const oldStep = currentSteps.filter((step) => step.token === targetReference.token);
  if (
    checkUpdate &&
    oldStep.length === 1 &&
    oldStep[0].type === WorkflowStepType.Action &&
    oldStep[0].action &&
    oldStep[0].action.key === targetReference.action.key
  ) {
    return step;
  }
  const action = step.action as WorkflowActionCreateCaseV1;
  const currentCaseObjects = action.params['case-objects'];
  const usableObjects = generateUsableObjects({
    inputStep,
    stepsToProcess: findPath(step.token, currentSteps).map((step) =>
      step.token === targetReference.token ? targetReference : step,
    ),
    activeStepToken: step.token,
    criteriaVariableSchemas,
  });
  const attachableTokens = caseAttachmentsFor(usableObjects)
    .map(({ value }) => value)
    .filter(
      (token) => token.startsWith(targetReference.token) && !currentCaseObjects.includes(token),
    );

  if (attachableTokens.length > 0) {
    return {
      ...step,
      action: {
        ...action,
        params: {
          ...action.params,
          'case-objects': [...currentCaseObjects, ...attachableTokens],
        },
      },
    };
  }
  return step;
};

const updateReferencesForLoopSetting = (
  settings: WorkflowStepLoop['settings'],
  tokenMap: Map<string, string>,
): WorkflowStepLoop['settings'] => {
  // TODO(maria): add validation for loop step to check for field's validity
  // then use the validation to decide what to update
  switch (settings.type) {
    case WorkflowStepLoopType.While:
      updateReferencesForConditional(settings['conditional'], tokenMap);
      break;
    case WorkflowStepLoopType.Map:
      settings['item-path'] = replaceTokenReference(settings['item-path'], tokenMap);
      settings['output-step-token'] = replaceTokenReference(
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        settings['output-step-token'],
        tokenMap,
      );
    // fall through on purpose
    case WorkflowStepLoopType.ForEach:
      settings['array-path'] = replaceTokenReference(settings['array-path'], tokenMap);
  }
  return settings;
};

const updateReferencesForConditionals = (
  conditionals: WorkflowStepConditional['conditionals'],
  tokenMap: Map<string, string>,
): WorkflowStepConditional['conditionals'] => {
  // TODO(maria): use the validation errors to decide which conditionals need to be updated
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  conditionals.slice(0, -1).forEach((conditional: WorkflowConditional) => {
    updateReferencesForConditional(conditional, tokenMap);
  });
  return conditionals;
};

const updateReferencesForConditional = (
  conditional: WorkflowConditional,
  tokenMap: Map<string, string>,
) => {
  conditional.rules?.or?.forEach((orRule) => {
    orRule.and?.forEach((andRule) => {
      Object.entries(andRule).forEach(([_, [left, right]]) => {
        [left, right].forEach((operand) => {
          if (Array.isArray(operand)) {
            (operand as JsonLogicField[]).forEach((r) => {
              if (r.var) {
                r.var = replaceTokenReference(r.var, tokenMap);
              }
            });
          }
          if (operand && isJsonLogicField(operand)) {
            const fieldRule = operand;
            fieldRule.var = replaceTokenReference(fieldRule.var, tokenMap);
          }
        });
      });
    });
  });
};
export const flattenSteps = (stepsConfig: WorkflowStep[]): WorkflowStep[] => {
  return flattenStepsHelper(stepsConfig);
};

const flattenStepsHelper = (stepsConfig: WorkflowStep[]): WorkflowStep[] => {
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  return stepsConfig.reduce((list, step) => {
    if (step.type === WorkflowStepType.Conditional || step.type === WorkflowStepType.Parallel) {
      return [...list, step, ...step.branches.flatMap((branch) => flattenStepsHelper(branch))];
    } else if (step.type === WorkflowStepType.Loop) {
      return [...list, step, ...flattenStepsHelper(step.branch)];
    } else {
      return [...list, step];
    }
  }, []);
};

export const getAllModuleSteps = (tree: WorkflowStep[]): WorkflowStepModule[] =>
  flattenSteps(tree).filter((s): s is WorkflowStepModule => s.type === WorkflowStepType.Module);

export const getAllModuleBlockIds = (tree: WorkflowStep[]) =>
  uniq(getAllModuleSteps(tree).map((ms) => ms['child-workflow-block-id']));
